import type { OwnProfileResponse } from './types/membership.js';

export interface UserCapabilities {
  readonly isReady: boolean;
  readonly allowedKeys: readonly string[];
  readonly roleKeys: readonly string[];
  readonly hasCapability: (key: string) => boolean;
  readonly canSettleAr: boolean;
  readonly canSettleAp: boolean;
  readonly isTenantAdmin: boolean;
}

export const DENY_ALL_CAPABILITIES: UserCapabilities = {
  isReady: false,
  allowedKeys: [],
  roleKeys: [],
  hasCapability: () => false,
  canSettleAr: false,
  canSettleAp: false,
  isTenantAdmin: false,
};

function normalizeKey(key: string): string {
  return key.trim().toLowerCase();
}

/**
 * Computes live DENY-first user capabilities from the profile payload.
 * Evaluates full tenant admin, server-returned capabilities.allowedKeys (NEW-03),
 * and role template defaults (BUG-04 / ACCOUNTANT_ROLE / FINANCE_APPROVER_ROLE).
 */
export function evaluateCapabilities(
  profile: OwnProfileResponse | null | undefined,
): UserCapabilities {
  if (!profile) {
    return DENY_ALL_CAPABILITIES;
  }

  const isTenantAdmin = Boolean(
    profile.canEditLockedFields ||
    profile.roleDisplayNames.some((r) => /مدیر|ادمین|admin/i.test(r)) ||
    profile.capabilities?.roleKeys.includes('tenant-admin'),
  );

  const allowedKeys = profile.capabilities?.allowedKeys ?? [];
  const roleKeys = profile.capabilities?.roleKeys ?? [];

  const hasExplicitKeys = allowedKeys.length > 0;

  function hasCapability(targetKey: string): boolean {
    if (isTenantAdmin) return true;

    const norm = normalizeKey(targetKey);
    const bareKey = norm.startsWith('accounting.') ? norm.slice('accounting.'.length) : norm;
    const modulePrefixed = `accounting.${bareKey}`;

    if (hasExplicitKeys) {
      if (allowedKeys.includes('*')) return true;
      if (
        allowedKeys.includes(norm) ||
        allowedKeys.includes(bareKey) ||
        allowedKeys.includes(modulePrefixed)
      ) {
        return true;
      }
      return false;
    }

    // Role-based fallback when server capabilities.allowedKeys is not yet populated (pre-NEW-03)
    const isAccountantOrApprover =
      roleKeys.includes('accountant') ||
      roleKeys.includes('finance-approver') ||
      profile?.roleDisplayNames.some((r) => /حسابدار|تأییدکننده/i.test(r));

    if (
      bareKey === 'ar-open-item.settle' ||
      bareKey === 'ap-open-item.settle' ||
      bareKey === 'ar-open-item.read' ||
      bareKey === 'ap-open-item.read'
    ) {
      return Boolean(isAccountantOrApprover);
    }

    // Pure treasurer role (خزانه‌دار) has treasury permissions but NOT settle permissions
    const isTreasurer =
      roleKeys.includes('treasurer') || profile?.roleDisplayNames.some((r) => /خزانه/i.test(r));

    if (bareKey.startsWith('treasury-')) {
      return Boolean(isAccountantOrApprover || isTreasurer);
    }

    return false;
  }

  const canSettleAr = hasCapability('accounting.ar-open-item.settle');
  const canSettleAp = hasCapability('accounting.ap-open-item.settle');

  return {
    isReady: true,
    allowedKeys,
    roleKeys,
    hasCapability,
    canSettleAr,
    canSettleAp,
    isTenantAdmin,
  };
}
